import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Send, Loader2, Clock, CheckCircle, XCircle, ShieldAlert } from "lucide-react";
import { usePermissions, submitOrganizerRequest } from "@/lib/permissions";

export const Route = createFileRoute("/request-organizer-access")({
  head: () => ({ meta: [{ title: "Solicitar acceso de organizador — ZYNTRA" }] }),
  component: RequestAccessPage,
});

function RequestAccessPage() {
  const perms = usePermissions();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (perms.loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!perms.user) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Iniciá sesión</h1>
        <Link to="/login" className="mt-4 inline-block text-sm text-primary hover:underline">
          Ir a Login
        </Link>
      </div>
    );
  }

  // Already an organizer
  if (perms.canCreateEventsAnywhere) {
    return (
      <div className="mx-auto max-w-xl px-4 pt-32">
        <div className="rounded-3xl border border-border bg-card p-8 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-success" />
          <h1 className="mt-3 text-xl font-bold text-foreground">Ya tenés permisos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Podés crear eventos en {perms.tenantsWithCreateEvents.length || 1} organización
            {perms.tenantsWithCreateEvents.length === 1 ? "" : "es"}.
          </p>
          <Link
            to="/create-event"
            className="mt-4 inline-block rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
          >
            Crear evento ahora
          </Link>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await submitOrganizerRequest(perms.user!.id, message);
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message ?? "No se pudo enviar la solicitud.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success || perms.hasPendingRequest) {
    return (
      <div className="mx-auto max-w-xl px-4 pt-32">
        <div className="rounded-3xl border border-border bg-card p-8 text-center">
          <Clock className="mx-auto h-12 w-12 text-warning" />
          <h1 className="mt-3 text-xl font-bold text-foreground">Solicitud enviada</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Un administrador la revisará pronto. Te avisaremos por email y notificación cuando se apruebe.
          </p>
          <button
            onClick={() => navigate({ to: "/" })}
            className="mt-5 rounded-xl bg-secondary px-5 py-2.5 text-sm font-bold text-foreground"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-4 pb-20 pt-24 sm:px-6">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver
      </Link>

      <h1 className="text-2xl font-bold text-foreground">Solicitar acceso de organizador</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Para crear eventos en ZYNTRA tenés que pertenecer a una organización con permiso. Contanos brevemente qué tipo de eventos
        querés organizar y un administrador revisará tu solicitud.
      </p>

      {perms.lastRejection && (
        <div className="mt-4 rounded-xl bg-destructive/10 p-4">
          <p className="flex items-center gap-2 text-sm font-bold text-destructive">
            <XCircle className="h-4 w-4" /> Solicitud anterior rechazada
          </p>
          {perms.lastRejection.admin_notes && (
            <p className="mt-1 text-xs text-muted-foreground">Motivo: {perms.lastRejection.admin_notes}</p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Mensaje (opcional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={1000}
            placeholder="Ej: Organizo conciertos de música electrónica en Buenos Aires. Tengo experiencia con venues de hasta 2000 personas..."
            className="mt-1.5 w-full rounded-xl border border-border bg-secondary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
          <p className="mt-1 text-xs text-muted-foreground">{message.length}/1000 caracteres</p>
        </div>

        {error && (
          <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {submitting ? "Enviando…" : "Enviar solicitud"}
        </button>
      </form>
    </div>
  );
}
